import React, { useMemo, useState, useEffect, useCallback } from 'react';
import {
  collection,
  doc,
  getDocs,
  query,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { Edit, Plus, Trash2, Users as UsersIcon, X, XCircle, CheckCircle2, AlertTriangle, KeyRound, ShieldCheck, ShieldAlert } from 'lucide-react';
import { auth, db } from '../../firebase';
import { useUserFacingError } from '../../hooks/useUserFacingError';
import { adminCancelUserSubscription, adminDeleteUser, adminDeleteAuthUser, adminListAuthUsers } from '../../api';
import { EcointelLottieMark } from '../../components/EcoIntelLoader';
import {
  ALL_ASSIGNABLE_ROLES,
  PLAN_TIERS,
  PLAN_TIER_BADGE_STYLES,
  PLAN_TIER_LABELS,
  ROLES,
  ROLE_BADGE_STYLES,
  ROLE_LABELS,
  SUBSCRIPTION_BADGE_STYLES,
  SUBSCRIPTION_LABELS,
  SUBSCRIPTION_STATUS,
  resolvePlanTier,
} from '../../constants/roles';
import {
  COMPLEXITY_LEVELS,
  VIEW_LEVEL_LABELS,
} from '../../context/ViewComplexityContext';
import AdminTable from '../../components/admin/AdminTable';
import { useAdminData } from '../../components/admin/AdminLayout';

const EMPTY_FORM = {
  email: '',
  businessName: '',
  phoneNumber: '',
  farmIds: [],
  role: ROLES.USER,
  subscriptionOverride: false,
  overrideReason: '',
  viewLevel: 'standard',
};

export default function UsersAdmin() {
  const { users, farms, refresh } = useAdminData();
  const { formatError } = useUserFacingError();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const [roleFilter, setRoleFilter] = useState([]);
  const [subFilter, setSubFilter] = useState([]);
  const [planFilter, setPlanFilter] = useState([]);
  const [farmsFilter, setFarmsFilter] = useState([]);
  const [overrideFilter, setOverrideFilter] = useState([]);

  // Admin-initiated Stripe cancellation state. Scoped to the open edit
  // modal because cancellation is a per-user action triggered from the
  // user's own edit form. `cancelInfo` mirrors the server response so
  // we can render a confirmation banner inline without needing to
  // refresh the modal (refresh() is also called so the underlying
  // users list updates once the Stripe webhook lands).
  const [cancelling, setCancelling] = useState(false);
  const [cancelError, setCancelError] = useState(null);
  const [cancelInfo, setCancelInfo] = useState(null);

  // Per-row delete-user state. Single id (not a set) because deletion
  // hits a server endpoint that cancels Stripe + deletes the doc and
  // we don't want the admin firing two simultaneously by accident.
  // `deleteError` is shown as a transient toast-style notice at the
  // bottom-right of the table; cleared automatically on next attempt.
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteInfo, setDeleteInfo] = useState(null);

  // Secondary Auth-only cleanup state. Driven from the delete-result banner
  // when the main delete left the Firebase Auth identity behind
  // (auth_deleted === false). `authCleanupBusy` blocks double-fires;
  // `authCleanupResult` lets the banner update in-place after the follow-up.
  const [authCleanupBusy, setAuthCleanupBusy] = useState(false);
  const [authCleanupResult, setAuthCleanupResult] = useState(null);

  // Firebase Auth index. Loaded once on mount (and after any delete) so each
  // table row can show whether a sign-in identity actually exists for that
  // user, and so we can surface orphaned Auth records (an Auth identity with
  // no users/{uid} doc — the leftovers from pre-v70.8 doc-only deletes).
  // `authIndex` is null until loaded; rows render a neutral "checking…" badge
  // in that window rather than guessing.
  const [authIndex, setAuthIndex] = useState(null);
  const [authIndexLoading, setAuthIndexLoading] = useState(false);
  const [authIndexError, setAuthIndexError] = useState(null);
  const [orphanBusyUid, setOrphanBusyUid] = useState(null);

  const loadAuthIndex = useCallback(async () => {
    setAuthIndexLoading(true);
    setAuthIndexError(null);
    try {
      const { users: authUsers } = await adminListAuthUsers();
      const uidSet = new Set();
      const emailSet = new Set();
      (authUsers || []).forEach((a) => {
        uidSet.add(a.uid);
        if (a.email) emailSet.add(a.email.toLowerCase());
      });
      setAuthIndex({ uidSet, emailSet, list: authUsers || [] });
    } catch (err) {
      setAuthIndexError(formatError(err));
      setAuthIndex(null);
    } finally {
      setAuthIndexLoading(false);
    }
  }, [formatError]);

  useEffect(() => { loadAuthIndex(); }, [loadAuthIndex]);

  // A row "has auth" if its doc id matches an Auth uid, or (for placeholder
  // docs whose id isn't the uid) its email matches an Auth identity. Returns
  // null while the index is still loading so the badge can show "checking…".
  const rowHasAuth = useCallback((u) => {
    if (!authIndex) return null;
    if (authIndex.uidSet.has(u.id)) return true;
    if (u.email && authIndex.emailSet.has(u.email.toLowerCase())) return true;
    return false;
  }, [authIndex]);

  // Auth identities with no corresponding Firestore user doc (matched by uid
  // OR email). These are the orphans the admin keeps finding in Firebase.
  const orphanAuthUsers = useMemo(() => {
    if (!authIndex) return [];
    const docIds = new Set((users || []).map((u) => u.id));
    const docEmails = new Set(
      (users || []).map((u) => (u.email || '').toLowerCase()).filter(Boolean)
    );
    return authIndex.list.filter((a) => {
      if (docIds.has(a.uid)) return false;
      if (a.email && docEmails.has(a.email.toLowerCase())) return false;
      return true;
    });
  }, [authIndex, users]);

  const handleRemoveOrphanAuth = async (uid, email) => {
    if (!uid || orphanBusyUid) return;
    const label = email || uid;
    const ok = window.confirm(
      `Remove the orphaned Firebase authentication record for ${label}?\n\n`
      + 'This sign-in identity has no profile in the app. Removing it means it can no longer authenticate. This cannot be undone.'
    );
    if (!ok) return;
    setOrphanBusyUid(uid);
    setAuthIndexError(null);
    try {
      await adminDeleteAuthUser(uid);
      await loadAuthIndex();
    } catch (err) {
      setAuthIndexError(`Failed to remove ${label}: ${formatError(err)}`);
    } finally {
      setOrphanBusyUid(null);
    }
  };

  const handleEdit = (u) => {
    setEditingUser(u);
    setFormData({
      ...EMPTY_FORM,
      ...u,
      role: u.role || ROLES.USER,
      subscriptionOverride: u.subscriptionOverride || false,
      overrideReason: u.overrideReason || '',
      viewLevel: u.viewLevel || 'standard',
    });
    setCancelError(null);
    setCancelInfo(null);
    setIsModalOpen(true);
  };

  const handleAddNew = () => {
    setEditingUser(null);
    setFormData(EMPTY_FORM);
    setCancelError(null);
    setCancelInfo(null);
    setIsModalOpen(true);
  };

  // Schedule cancel-at-period-end on the target user's Stripe
  // subscription via the analytics-server admin endpoint. Visibility of
  // the button below is driven by `editingUser` (the original Firestore
  // snapshot from when the modal opened), not `formData`, so toggling
  // the override checkbox in the form doesn't accidentally hide the
  // button before the admin saves.
  const handleCancelUserSubscription = async () => {
    if (!editingUser || cancelling) return;
    const label = editingUser.email || editingUser.businessName || editingUser.id;
    const ok = window.confirm(
      `Cancel ${label}'s Stripe subscription?\n\n`
      + 'This schedules cancel-at-period-end. They will retain access until the end of their current billing period, then automatically lose paid features.'
    );
    if (!ok) return;
    setCancelling(true);
    setCancelError(null);
    setCancelInfo(null);
    try {
      const result = await adminCancelUserSubscription(editingUser.id);
      setCancelInfo(result);
      // Refresh the underlying users list so the list view reflects
      // the new status (the Stripe webhook will flip
      // subscriptionStatus → 'cancelling' shortly; refresh now picks
      // that up as soon as Firestore has it). Local `editingUser`
      // stays stale on purpose — the inline success banner gives the
      // admin clear feedback; reopening the modal reads fresh data.
      refresh();
    } catch (err) {
      setCancelError(formatError(err));
    } finally {
      setCancelling(false);
    }
  };

  // Full user deletion. Calls the backend `POST /admin/delete-user`
  // endpoint which:
  //   (1) cancels the target user's Stripe subscription IMMEDIATELY
  //       (regardless of whether it's `active` or `cancelling`),
  //   (2) deletes the user doc in Firestore atomically,
  //   (3) sweeps the user's in-flight intake draft, if any.
  // The local pre-flight self-check is a UX nicety — the server also
  // refuses self-delete with `self_delete_blocked`, but the inline
  // alert here means the admin gets instant feedback without a
  // network round-trip and the confirmation dialog never shows for
  // their own row.
  const handleDeleteUser = async (id) => {
    if (deletingUserId) return;
    const callerUid = auth.currentUser?.uid;
    if (callerUid && callerUid === id) {
      window.alert('You can’t delete your own admin account from this console. Ask another admin to do it.');
      return;
    }
    const target = (users || []).find((u) => u.id === id) || {};
    const label = target.email || target.businessName || id;
    const hasStripeSub = !!target.stripeSubscriptionId;
    const subStatus = target.subscriptionStatus || '';
    // Tailor the confirmation language to what's actually about to
    // happen — important because cancellation is IMMEDIATE here
    // (unlike the "Cancel Subscription" button in the edit modal,
    // which schedules cancel-at-period-end). The admin needs to know
    // billing stops right now, not at the next renewal date.
    const subClause = hasStripeSub
      ? `\n\n⚠️ This will IMMEDIATELY cancel their Stripe subscription${subStatus ? ` (currently ${subStatus})` : ''}. They lose paid access right away, with no proration.`
      : '\n\nNo Stripe subscription on file. Firestore record will be removed.';
    const ok = window.confirm(
      `Permanently delete ${label}?${subClause}\n\nThis cannot be undone.`
    );
    if (!ok) return;

    setDeletingUserId(id);
    setDeleteError(null);
    setDeleteInfo(null);
    setAuthCleanupResult(null);
    try {
      const result = await adminDeleteUser(id);
      setDeleteInfo(result);
      refresh();
      loadAuthIndex();
    } catch (err) {
      setDeleteError(`Failed to delete ${label}: ${formatError(err)}`);
    } finally {
      setDeletingUserId(null);
    }
  };

  // Secondary, Auth-only destructive follow-up. Surfaced from the delete
  // banner when the main delete couldn't remove the Firebase Auth identity
  // (auth_deleted === false). Calls POST /admin/delete-auth-user, which
  // re-checks existence and deletes the Auth user if it's still there.
  const handleDeleteAuthUser = async (uid, email) => {
    if (!uid || authCleanupBusy) return;
    const label = email || uid;
    const ok = window.confirm(
      `Remove the Firebase authentication record for ${label}?\n\n`
      + 'Their Firestore profile is already deleted. This removes the leftover sign-in identity so they can no longer authenticate. This cannot be undone.'
    );
    if (!ok) return;
    setAuthCleanupBusy(true);
    try {
      const result = await adminDeleteAuthUser(uid);
      setAuthCleanupResult(result);
      loadAuthIndex();
    } catch (err) {
      setAuthCleanupResult({ status: 'auth_delete_failed', auth_deleted: false, auth_delete_error: formatError(err) });
    } finally {
      setAuthCleanupBusy(false);
    }
  };

  const toggleFarm = (farmId) => {
    setFormData((prev) => {
      const current = prev.farmIds || [];
      return current.includes(farmId)
        ? { ...prev, farmIds: current.filter((f) => f !== farmId) }
        : { ...prev, farmIds: [...current, farmId] };
    });
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    const dataToSave = {
      email: formData.email,
      businessName: formData.businessName || '',
      phoneNumber: formData.phoneNumber || '',
      farmIds: formData.farmIds || [],
      role: formData.role || ROLES.USER,
      subscriptionOverride: formData.subscriptionOverride || false,
      overrideReason: formData.subscriptionOverride ? (formData.overrideReason || '') : '',
      viewLevel: formData.viewLevel || 'standard',
    };
    try {
      if (editingUser) {
        await updateDoc(doc(db, 'users', editingUser.id), dataToSave);
      } else {
        const q = query(collection(db, 'users'), where('email', '==', formData.email));
        const snap = await getDocs(q);
        if (snap.size > 0) {
          const existingDoc = snap.docs[0];
          await updateDoc(doc(db, 'users', existingDoc.id), dataToSave);
        } else {
          const placeholderId = `pending_${Date.now()}`;
          await setDoc(doc(db, 'users', placeholderId), { ...dataToSave, createdAt: new Date().toISOString() });
        }
      }
      setIsModalOpen(false);
      refresh();
    } catch (err) {
      alert(`Save failed: ${formatError(err)}`);
    }
  };

  const filteredRows = useMemo(() => {
    return (users || []).filter((u) => {
      if (roleFilter.length && !roleFilter.includes(u.role || ROLES.USER)) return false;
      const subStatus = u.subscriptionStatus || SUBSCRIPTION_STATUS.NONE;
      if (subFilter.length && !subFilter.includes(subStatus)) return false;
      const tier = resolvePlanTier(u);
      if (planFilter.length && !planFilter.includes(tier)) return false;
      const hasFarms = (u.farmIds || []).length > 0;
      if (farmsFilter.length && !farmsFilter.includes(hasFarms ? 'has' : 'none')) return false;
      if (overrideFilter.length) {
        const ov = u.subscriptionOverride === true ? 'override' : 'no_override';
        if (!overrideFilter.includes(ov)) return false;
      }
      return true;
    });
  }, [users, roleFilter, subFilter, planFilter, farmsFilter, overrideFilter]);

  const filterGroups = [
    {
      id: 'role',
      label: 'Role',
      selected: roleFilter,
      onChange: setRoleFilter,
      options: ALL_ASSIGNABLE_ROLES.map((r) => ({ value: r, label: ROLE_LABELS[r] })),
    },
    {
      id: 'subscription',
      label: 'Subscription',
      selected: subFilter,
      onChange: setSubFilter,
      options: Object.values(SUBSCRIPTION_STATUS).map((s) => ({ value: s, label: SUBSCRIPTION_LABELS[s] })),
    },
    {
      id: 'plan',
      label: 'Plan tier',
      selected: planFilter,
      onChange: setPlanFilter,
      options: Object.values(PLAN_TIERS).map((t) => ({ value: t, label: PLAN_TIER_LABELS[t] })),
    },
    {
      id: 'farms',
      label: 'Properties',
      selected: farmsFilter,
      onChange: setFarmsFilter,
      options: [
        { value: 'has', label: 'Has properties' },
        { value: 'none', label: 'No properties' },
      ],
    },
    {
      id: 'override',
      label: 'Override',
      selected: overrideFilter,
      onChange: setOverrideFilter,
      options: [
        { value: 'override', label: 'Has override' },
        { value: 'no_override', label: 'No override' },
      ],
    },
  ];

  const columns = [
    {
      key: 'createdAt',
      label: 'Signed up',
      sortValue: (u) => Date.parse(u.createdAt) || 0,
    },
    { key: 'business', label: 'Business', sortValue: (u) => (u.businessName || u.displayName || '').toLowerCase() },
    { key: 'email', label: 'Email', sortValue: (u) => (u.email || '').toLowerCase() },
    { key: 'role', label: 'Role / access', sortValue: (u) => ROLE_LABELS[u.role] || 'User' },
    { key: 'subscription', label: 'Subscription', sortValue: (u) => SUBSCRIPTION_LABELS[u.subscriptionStatus || SUBSCRIPTION_STATUS.NONE] },
    { key: 'plan', label: 'Plan', sortValue: (u) => PLAN_TIER_LABELS[resolvePlanTier(u)] },
    { key: 'properties', label: 'Properties', sortValue: (u) => (u.farmIds || []).length, align: 'right', className: 'text-right' },
    { key: 'auth', label: 'Sign-in', sortValue: (u) => { const a = rowHasAuth(u); return a === true ? 0 : a === false ? 1 : 2; } },
    { key: 'actions', label: 'Actions', sortable: false, align: 'right', className: 'text-right' },
  ];

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3 text-slate-900">
            <UsersIcon size={22} /> Users
          </h1>
          <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5">
            {users.length} total · {filteredRows.length} match current filters
            {authIndexLoading && (
              <span className="inline-flex items-center gap-1 text-slate-400">
                · <KeyRound size={11} /> checking sign-in identities…
              </span>
            )}
          </p>
        </div>
        <button
          onClick={handleAddNew}
          className="bg-[#043132] text-white px-4 py-2.5 rounded-lg flex items-center gap-2 hover:bg-[#065354] font-medium text-sm transition-colors"
        >
          <Plus size={16} /> Add User
        </button>
      </div>

      {/* Delete-user result banner. Lives above the table so the
          admin can't miss it; dismissible because the underlying row
          is already gone from `refresh()`. We deliberately surface
          BOTH the success case and any partial-failure detail (e.g.
          Stripe sub already cancelled on Stripe's side — non-fatal
          but worth telling the admin so they know to double-check
          Stripe if they were expecting an active sub). */}
      {deleteError && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <div className="flex items-start gap-2">
            <XCircle size={16} className="shrink-0 mt-0.5 text-red-600" />
            <span>{deleteError}</span>
          </div>
          <button onClick={() => setDeleteError(null)} className="text-red-500 hover:text-red-700 shrink-0">
            <X size={14} />
          </button>
        </div>
      )}
      {deleteInfo && (() => {
        // The Auth identity is resolved (gone) if the cleanup follow-up
        // succeeded, or if the main delete already removed it / found none.
        const authResolved =
          (authCleanupResult && authCleanupResult.auth_deleted !== false) ||
          deleteInfo.auth_deleted === true ||
          deleteInfo.auth_deleted === null;
        // Show the secondary "remove auth" action only when the main delete
        // left the Auth identity behind and we haven't since cleaned it up.
        const showAuthCleanup = deleteInfo.auth_deleted === false && !authResolved;
        const bannerTone = showAuthCleanup
          ? 'border-amber-200 bg-amber-50 text-amber-900'
          : 'border-emerald-200 bg-emerald-50 text-emerald-800';
        return (
        <div className={`mb-4 flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${bannerTone}`}>
          <div className="flex items-start gap-2">
            {showAuthCleanup
              ? <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-600" />
              : <CheckCircle2 size={16} className="shrink-0 mt-0.5 text-emerald-600" />}
            <div>
              <p className="font-semibold">
                Deleted {deleteInfo.target_email || deleteInfo.target_uid}
              </p>
              {deleteInfo.stripe_cancelled === true && (
                <p className="text-xs mt-0.5">
                  Stripe subscription <code className="font-mono">{deleteInfo.stripe_subscription_id}</code> cancelled immediately.
                </p>
              )}
              {deleteInfo.stripe_cancelled === false && (
                <p className="text-xs mt-0.5 text-amber-700">
                  Note: Stripe cancellation reported an error: {deleteInfo.stripe_cancel_error || 'unknown'}. The user doc was deleted; verify the subscription is closed in the Stripe dashboard.
                </p>
              )}
              {deleteInfo.stripe_cancelled === null && (
                <p className="text-xs mt-0.5 text-slate-600">No Stripe subscription on file.</p>
              )}

              {/* Firebase Auth outcome */}
              {deleteInfo.auth_deleted === true && (
                <p className="text-xs mt-0.5">Firebase authentication record removed.</p>
              )}
              {deleteInfo.auth_deleted === null && (
                <p className="text-xs mt-0.5 text-slate-600">No Firebase authentication record found (already removed).</p>
              )}
              {deleteInfo.auth_deleted === false && !authResolved && (
                <div className="mt-1.5">
                  <p className="text-xs text-amber-800">
                    Firebase authentication could NOT be removed automatically{deleteInfo.auth_delete_error ? `: ${deleteInfo.auth_delete_error}` : '.'} The sign-in identity still exists. Remove it below so this account can no longer authenticate.
                  </p>
                  <button
                    type="button"
                    onClick={() => handleDeleteAuthUser(deleteInfo.target_uid, deleteInfo.target_email)}
                    disabled={authCleanupBusy}
                    className="mt-2 inline-flex items-center gap-2 rounded-md border border-red-300 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {authCleanupBusy
                      ? <EcointelLottieMark size={14} />
                      : <Trash2 size={14} />}
                    Remove Firebase authentication
                  </button>
                </div>
              )}
              {/* Follow-up result of the secondary cleanup */}
              {authCleanupResult && authCleanupResult.auth_deleted === true && (
                <p className="text-xs mt-1.5 text-emerald-700">Firebase authentication record removed.</p>
              )}
              {authCleanupResult && authCleanupResult.status === 'auth_user_absent' && (
                <p className="text-xs mt-1.5 text-emerald-700">No Firebase authentication record found (already removed).</p>
              )}
              {authCleanupResult && authCleanupResult.auth_deleted === false && (
                <p className="text-xs mt-1.5 text-red-700">
                  Still could not remove Firebase authentication{authCleanupResult.auth_delete_error ? `: ${authCleanupResult.auth_delete_error}` : '.'} Check the runtime service account has the Firebase Authentication Admin role, then retry or remove via the Firebase Console.
                </p>
              )}
            </div>
          </div>
          <button onClick={() => { setDeleteInfo(null); setAuthCleanupResult(null); }} className="text-slate-500 hover:text-slate-700 shrink-0">
            <X size={14} />
          </button>
        </div>
        );
      })()}

      {authIndexError && (
        <div className="mb-4 flex items-start justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <div className="flex items-start gap-2">
            <XCircle size={16} className="shrink-0 mt-0.5 text-red-600" />
            <span>Firebase Authentication check: {authIndexError}</span>
          </div>
          <button onClick={() => loadAuthIndex()} className="text-red-600 hover:text-red-800 shrink-0 text-xs font-semibold underline">
            Retry
          </button>
        </div>
      )}

      {/* Orphaned Auth banner. Auth identities with no Firestore profile —
          the leftovers from pre-v70.8 deletes that removed the doc but not the
          sign-in identity. Surfaced here so the admin can clean them up
          without leaving the console. */}
      {orphanAuthUsers.length > 0 && (
        <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div className="flex items-start gap-2">
            <AlertTriangle size={16} className="shrink-0 mt-0.5 text-amber-600" />
            <div className="flex-1">
              <p className="font-semibold">
                {orphanAuthUsers.length} orphaned Firebase {orphanAuthUsers.length === 1 ? 'identity' : 'identities'} (no app profile)
              </p>
              <p className="text-xs mt-0.5 text-amber-800">
                These can still sign in but have no <code className="font-mono">users</code> doc. Remove the ones that were deleted intentionally.
              </p>
              <ul className="mt-2 space-y-1.5">
                {orphanAuthUsers.map((a) => (
                  <li key={a.uid} className="flex items-center justify-between gap-3 rounded-md bg-white/70 border border-amber-200 px-2.5 py-1.5">
                    <div className="min-w-0">
                      <div className="text-xs font-medium text-slate-800 truncate">{a.email || '(no email)'}</div>
                      <div className="text-[10px] text-slate-500 truncate">
                        <span className="font-mono">{a.uid}</span>
                        {a.lastSignInTime ? ` · last sign-in ${new Date(a.lastSignInTime).toLocaleDateString()}` : ' · never signed in'}
                        {a.providers?.length ? ` · ${a.providers.join(', ')}` : ''}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveOrphanAuth(a.uid, a.email)}
                      disabled={!!orphanBusyUid}
                      className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-red-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {orphanBusyUid === a.uid ? <EcointelLottieMark size={12} /> : <Trash2 size={12} />}
                      Remove
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <AdminTable
        rows={filteredRows}
        columns={columns}
        defaultSort={{ key: 'createdAt', direction: 'desc' }}
        rowKey={(u) => u.id}
        searchPlaceholder="Search by name, email, business, role…"
        searchPredicate={(u, q) => (
          (u.email || '').toLowerCase().includes(q) ||
          (u.businessName || '').toLowerCase().includes(q) ||
          (u.displayName || '').toLowerCase().includes(q) ||
          (u.phoneNumber || '').toLowerCase().includes(q) ||
          (ROLE_LABELS[u.role] || '').toLowerCase().includes(q)
        )}
        filterGroups={filterGroups}
        defaultPageSize={25}
        emptyMessage="No users found."
        csvExport={{
          filename: 'users.csv',
          header: ['Signed up', 'Email', 'Business', 'Display name', 'Phone', 'Role', 'Subscription', 'Plan tier', 'Property count', 'Override'],
          mapRow: (u) => [
            u.createdAt ? new Date(u.createdAt).toISOString() : '',
            u.email,
            u.businessName,
            u.displayName,
            u.phoneNumber,
            ROLE_LABELS[u.role] || 'User',
            SUBSCRIPTION_LABELS[u.subscriptionStatus || SUBSCRIPTION_STATUS.NONE],
            PLAN_TIER_LABELS[resolvePlanTier(u)],
            (u.farmIds || []).length,
            u.subscriptionOverride ? 'yes' : 'no',
          ],
        }}
        renderRow={(u) => {
          const isPending = u.id.startsWith('pending_') || u.id.startsWith('user_');
          const roleStyle = ROLE_BADGE_STYLES[u.role] || ROLE_BADGE_STYLES[ROLES.USER];
          const subStatus = u.subscriptionStatus || SUBSCRIPTION_STATUS.NONE;
          const subStyle = SUBSCRIPTION_BADGE_STYLES[subStatus] || SUBSCRIPTION_BADGE_STYLES[SUBSCRIPTION_STATUS.NONE];
          const planTier = resolvePlanTier(u);
          const planStyle = PLAN_TIER_BADGE_STYLES[planTier];
          return (
            <>
              <td className="p-4 whitespace-nowrap">
                <div className="text-xs text-slate-700">
                  {u.createdAt ? new Date(u.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—'}
                </div>
              </td>
              <td className="p-4">
                <div className="font-medium text-slate-800">{u.businessName || u.displayName || 'N/A'}</div>
                {isPending && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">Pending login</span>}
              </td>
              <td className="p-4">
                <div className="text-slate-700">{u.email}</div>
                <div className="text-xs text-slate-400">{u.phoneNumber}</div>
              </td>
              <td className="p-4">
                <div className="flex flex-wrap gap-1">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${roleStyle}`}>{ROLE_LABELS[u.role] || 'User'}</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${u.viewLevel === 'technical' ? 'bg-violet-100 text-violet-700' : u.viewLevel === 'essential' ? 'bg-slate-100 text-slate-500' : 'bg-emerald-50 text-emerald-600'}`}>
                    {VIEW_LEVEL_LABELS[u.viewLevel] || 'Standard'}
                  </span>
                </div>
              </td>
              <td className="p-4">
                {u.role !== ROLES.ADMIN ? (
                  u.subscriptionOverride ? (
                    <span className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-medium" title={u.overrideReason || 'Admin override'}>
                      Override
                    </span>
                  ) : (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${subStyle}`}>{SUBSCRIPTION_LABELS[subStatus]}</span>
                  )
                ) : (
                  <span className="text-[10px] text-slate-400">—</span>
                )}
              </td>
              <td className="p-4">
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${planStyle}`}>{PLAN_TIER_LABELS[planTier]}</span>
              </td>
              <td className="p-4 text-right">
                <div className="text-xs text-slate-600">{u.farmIds?.length || 0} assigned</div>
                {u.farmIds?.length > 0 && (
                  <div className="text-[10px] text-slate-400 mt-0.5 truncate max-w-[200px] ml-auto">{u.farmIds.join(', ')}</div>
                )}
              </td>
              <td className="p-4">
                {(() => {
                  const a = rowHasAuth(u);
                  if (a === null) {
                    return <span className="text-[10px] text-slate-400" title="Checking Firebase Authentication…">checking…</span>;
                  }
                  if (a === true) {
                    return (
                      <span className="inline-flex items-center gap-1 text-[10px] bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded font-medium" title="A Firebase sign-in identity exists for this user.">
                        <ShieldCheck size={11} /> Auth
                      </span>
                    );
                  }
                  return (
                    <span className="inline-flex items-center gap-1 text-[10px] bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded font-medium" title="No Firebase sign-in identity. This profile cannot currently log in.">
                      <ShieldAlert size={11} /> No sign-in
                    </span>
                  );
                })()}
              </td>
              <td className="p-4 text-right">
                <button onClick={() => handleEdit(u)} className="mr-2 p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors" disabled={!!deletingUserId}><Edit size={16} /></button>
                <button
                  onClick={() => handleDeleteUser(u.id)}
                  disabled={!!deletingUserId}
                  title={u.stripeSubscriptionId
                    ? `Delete user: will IMMEDIATELY cancel their Stripe subscription (${u.subscriptionStatus || 'unknown status'}).`
                    : 'Delete user: no Stripe subscription on file.'}
                  className="p-1.5 text-slate-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {deletingUserId === u.id ? <EcointelLottieMark size={16} /> : <Trash2 size={16} />}
                </button>
              </td>
            </>
          );
        }}
      />

      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 backdrop-blur-sm" onClick={() => setIsModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-lg">{editingUser ? 'Edit' : 'Add'} User</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-slate-400 hover:text-slate-600"><X size={18} /></button>
            </div>
            <form onSubmit={handleSaveUser} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Email *</label>
                <input required type="email" placeholder="user@example.com" className="w-full border border-slate-200 p-2.5 rounded-lg text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Business Name</label>
                  <input placeholder="Business" className="w-full border border-slate-200 p-2.5 rounded-lg text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none" value={formData.businessName} onChange={(e) => setFormData({ ...formData, businessName: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Phone</label>
                  <input placeholder="Phone number" className="w-full border border-slate-200 p-2.5 rounded-lg text-sm focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none" value={formData.phoneNumber} onChange={(e) => setFormData({ ...formData, phoneNumber: e.target.value })} />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Role</label>
                <select
                  className="w-full border border-slate-200 p-2.5 rounded-lg text-sm bg-white focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                  value={formData.role || ROLES.USER}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                >
                  {ALL_ASSIGNABLE_ROLES.map((r) => (
                    <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">
                  {formData.role === ROLES.ADMIN && 'Full access to all properties, users, and system settings'}
                  {formData.role === ROLES.SUPERUSER && 'Can manage associated users and access multiple properties'}
                  {formData.role === ROLES.USER && 'Standard access to assigned properties only'}
                  {formData.role === ROLES.DEMO && 'Read-only access to assigned showcase properties'}
                </p>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">View Detail Level</label>
                <select
                  className="w-full border border-slate-200 p-2.5 rounded-lg text-sm bg-white focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                  value={formData.viewLevel || 'standard'}
                  onChange={(e) => setFormData({ ...formData, viewLevel: e.target.value })}
                >
                  {COMPLEXITY_LEVELS.map((l) => (
                    <option key={l} value={l}>{VIEW_LEVEL_LABELS[l]}</option>
                  ))}
                </select>
                <p className="text-[11px] text-slate-400 mt-1">
                  {formData.viewLevel === 'essential' && 'Simplified view, core metrics only'}
                  {formData.viewLevel === 'standard' && 'Default view, includes growing conditions and outcomes'}
                  {formData.viewLevel === 'technical' && 'Full view, includes calibration, data quality, and dataset audits'}
                </p>
              </div>

              <div className="border border-slate-200 rounded-lg p-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.subscriptionOverride || false}
                    onChange={(e) => setFormData({ ...formData, subscriptionOverride: e.target.checked })}
                    className="w-4 h-4 rounded border-slate-300 text-[#043132] focus:ring-[#043132]"
                  />
                  <span className="text-sm font-medium text-slate-700">Grant free access (admin override)</span>
                </label>
                {formData.subscriptionOverride && (
                  <input
                    placeholder="Reason for override (e.g., Co-founding associate, beta tester)"
                    className="w-full border border-slate-200 p-2 rounded-lg text-sm mt-2 focus:border-[#043132] focus:ring-1 focus:ring-[#043132] outline-none"
                    value={formData.overrideReason || ''}
                    onChange={(e) => setFormData({ ...formData, overrideReason: e.target.value })}
                  />
                )}
              </div>

              {/* Admin-initiated Stripe cancellation. Mirrors the gating
                  on the customer-facing UserProfile cancel button: shown
                  only for non-admin users with no override + an active
                  Stripe subscription on record. Override accounts are
                  handled via the checkbox above instead — there's no
                  Stripe sub to cancel on those, and the server endpoint
                  returns 400 `override_account` if attempted. Visibility
                  is driven by `editingUser` (the snapshot from when the
                  modal opened) so toggling the override checkbox in the
                  form above doesn't accidentally hide this affordance
                  before the admin has saved. */}
              {editingUser
                && editingUser.role !== ROLES.ADMIN
                && editingUser.subscriptionOverride !== true
                && !!editingUser.stripeSubscriptionId
                && editingUser.subscriptionStatus === SUBSCRIPTION_STATUS.ACTIVE
                && (
                  <div className="border border-red-200 bg-red-50/40 rounded-lg p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-800">Cancel Stripe subscription</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          Schedules cancel-at-period-end. The user keeps access until the end of their current billing period, then automatically loses paid features. Status flips to <span className="font-mono">cancelling</span> as soon as Stripe acknowledges; final <span className="font-mono">cancelled</span> arrives via webhook at period end.
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={handleCancelUserSubscription}
                        disabled={cancelling || !!cancelInfo}
                        className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-300 text-red-700 bg-white hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        {cancelling ? <EcointelLottieMark size={12} /> : <XCircle size={12} />}
                        {cancelling ? 'Cancelling…' : 'Cancel subscription'}
                      </button>
                    </div>
                    {cancelError && (
                      <p className="text-[11px] text-red-700 mt-2">{cancelError}</p>
                    )}
                    {cancelInfo && (
                      <div className="mt-2 flex items-start gap-1.5 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded p-2">
                        <CheckCircle2 size={12} className="mt-0.5 shrink-0" />
                        <span>
                          Cancellation scheduled for <span className="font-medium">{cancelInfo.target_email || cancelInfo.target_uid}</span>. The user will keep access until the end of their current billing period.
                        </span>
                      </div>
                    )}
                  </div>
                )}
              {editingUser
                && editingUser.role !== ROLES.ADMIN
                && editingUser.subscriptionOverride === true
                && !!editingUser.stripeSubscriptionId
                && (
                  <div className="border border-amber-200 bg-amber-50/60 rounded-lg p-3 text-[11px] text-amber-800">
                    This user is on an admin override. To remove paid access, untick <span className="font-medium">Grant free access (admin override)</span> above and save. If you also need to cancel a real Stripe subscription on this account, first untick the override and save, then reopen this modal to access the cancel button.
                  </div>
                )}

              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Assign Properties</label>
                <div className="border border-slate-200 rounded-lg p-2 max-h-40 overflow-y-auto">
                  {(farms || []).map((f) => (
                    <div key={f.id} onClick={() => toggleFarm(f.id)} className={`p-2 mb-1 rounded cursor-pointer text-sm flex items-center gap-2 transition-colors ${formData.farmIds?.includes(f.id) ? 'bg-[#043132] text-white' : 'hover:bg-slate-100 text-slate-700'}`}>
                      <div className={`w-4 h-4 border-2 rounded flex items-center justify-center ${formData.farmIds?.includes(f.id) ? 'border-white bg-white/20' : 'border-slate-300'}`}>
                        {formData.farmIds?.includes(f.id) && <div className="w-2 h-2 bg-white rounded-sm" />}
                      </div>
                      {f.farmName || f.name || f.id}
                    </div>
                  ))}
                  {(formData.farmIds || []).filter((fid) => !(farms || []).some((f) => f.id === fid)).map((orphanId) => (
                    <div key={orphanId} onClick={() => toggleFarm(orphanId)} className="p-2 mb-1 rounded cursor-pointer text-sm flex items-center gap-2 bg-red-50 text-red-700 border border-red-200">
                      <div className="w-4 h-4 border-2 border-red-400 bg-red-100 rounded flex items-center justify-center">
                        <div className="w-2 h-2 bg-red-500 rounded-sm" />
                      </div>
                      <span className="flex-1">{orphanId}</span>
                      <span className="text-[10px] bg-red-100 text-red-600 px-1.5 py-0.5 rounded font-medium">Deleted</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 text-slate-500 hover:text-slate-700 text-sm font-medium">Cancel</button>
                <button type="submit" className="px-6 py-2.5 bg-[#043132] text-white rounded-lg hover:bg-[#065354] font-medium text-sm transition-colors">Save User</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
